import React, { useState, useEffect , } from 'react';
import { Link,useNavigate } from 'react-router-dom';
import { talentService } from '../../../api/apiService';
import { lanoService } from '../../../api/lanoService';
import '../../../App.css';
import './TalentListSimple.css';

/**
 * Lightweight talent list: uses /talents/list (no goodPerformanceCompany etc).
 * Faster to load; use for browsing and linking to Billing edit.
 */
function TalentListSimple() {
  const navigate = useNavigate();
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, total: 0, limit: 100 });
  const [syncingId, setSyncingId] = useState(null);
  const [syncMessage, setSyncMessage] = useState({ id: null, type: '', text: '' });
  const [syncAllProgress, setSyncAllProgress] = useState({ running: false, current: 0, total: 0, success: 0, failed: 0 });

  const loadPage = async (page = 1, search) => {
    const term = search !== undefined ? search : searchName;
    setLoading(true);
    setError(null);
    try {
      const res = await talentService.getTalentsList(page, pagination.limit, term);
      const data = res.data;
      setTalents(data.talents || []);
      if (data.pagination) {
        setPagination(prev => ({
          ...prev,
          currentPage: data.pagination.currentPage ?? page,
          totalPages: data.pagination.totalPages ?? 1,
          total: data.pagination.total ?? 0,
          limit: data.pagination.limit ?? 100
        }));
      }
    } catch (err) {
      console.error('Error loading talent list:', err);
      setError(err.message || 'Failed to load talents');
      setTalents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPage(1, searchName);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    loadPage(1, searchName);
  };

  const vatStatusLabel = (s) => {
    if (!s) return '—';
    const map = { with_vat: '19 %', without_vat: 'No VAT', kleinunternehmer: 'Kleinunternehmer', auslander: 'Ausländer', divided: '2 CNs' };
    return map[s] || s;
  };
  const agencyVatLabel = (s) => {
    if (!s) return '—';
    const map = { with_vat: '19 %', without_vat: 'No VAT' };
    return map[s] || s;
  };

  const handlePage = (page) => {
    if (page < 1 || page > pagination.totalPages) return;
    loadPage(page, searchName);
  };

  const handleSyncAgency = async (talent) => {
    const lanoId = talent.Lano_ID || talent.lano_id;

    if (!lanoId) {
      setSyncMessage({ id: talent.id, type: 'error', text: 'No Lano ID' });
      return;
    }

    setSyncingId(talent.id);
    setSyncMessage({ id: null, type: '', text: '' });

    try {
      // Get Lano token
      const token = await lanoService.ensureToken();

      // Fetch freelancer data from Lano
      const response = await lanoService.getFreelancerById(token, lanoId);

      if (response && response.data && response.data.attributes) {
        const { isVendor, companyName } = response.data.attributes;

        // Update talent with agency info
        await talentService.updateTalent(talent.id, {
          is_agency: isVendor === true,
          Agentur: companyName || ''
        });

        // Update local state
        setTalents(prev => prev.map(t =>
          t.id === talent.id
            ? { ...t, is_agency: isVendor === true, Agentur: companyName || '' }
            : t
        ));

        setSyncMessage({
          id: talent.id,
          type: 'success',
          text: `Synced: ${isVendor ? 'Agency' : 'Not Agency'}${companyName ? ` - ${companyName}` : ''}`
        });
      } else {
        setSyncMessage({ id: talent.id, type: 'error', text: 'No data from Lano' });
      }
    } catch (err) {
      console.error('Error syncing agency:', err);
      setSyncMessage({ id: talent.id, type: 'error', text: err.message || 'Sync failed' });
    } finally {
      setSyncingId(null);
    }
  };

  const handleSyncAll = async () => {
    // Filter talents that have Lano IDs
    const talentsWithLanoId = talents.filter(t => t.Lano_ID || t.lano_id);

    if (talentsWithLanoId.length === 0) {
      alert('No talents with Lano ID on this page');
      return;
    }

    setSyncAllProgress({ running: true, current: 0, total: talentsWithLanoId.length, success: 0, failed: 0 });

    try {
      const token = await lanoService.ensureToken();
      let successCount = 0;
      let failedCount = 0;

      for (let i = 0; i < talentsWithLanoId.length; i++) {
        const talent = talentsWithLanoId[i];
        const lanoId = talent.Lano_ID || talent.lano_id;

        try {
          const response = await lanoService.getFreelancerById(token, lanoId);

          if (response && response.data && response.data.attributes) {
            const { isVendor, companyName } = response.data.attributes;

            await talentService.updateTalent(talent.id, {
              is_agency: isVendor === true,
              Agentur: companyName || ''
            });

            // Update local state
            setTalents(prev => prev.map(t =>
              t.id === talent.id
                ? { ...t, is_agency: isVendor === true, Agentur: companyName || '' }
                : t
            ));

            successCount++;
          } else {
            failedCount++;
          }
        } catch (err) {
          console.error(`Error syncing talent ${talent.id}:`, err);
          failedCount++;
        }

        setSyncAllProgress(prev => ({
          ...prev,
          current: i + 1,
          success: successCount,
          failed: failedCount
        }));
      }

      setSyncAllProgress(prev => ({ ...prev, running: false }));
    } catch (err) {
      console.error('Error in sync all:', err);
      setSyncAllProgress(prev => ({ ...prev, running: false }));
      alert('Failed to sync: ' + (err.message || 'Unknown error'));
    }
  };

  const display = (t) => (t && t !== 'null' && t !== 'undefined' ? String(t) : '—');

  if (loading && talents.length === 0) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1>Talent list (simple)</h1>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Talent list (simple)</h1>
        <p className="talent-list-simple-intro">
          Light list without company relationships. Use for quick lookup and billing edit.
        </p>
        <form onSubmit={handleSearch} className="talent-list-simple-search">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            className="talent-list-simple-search-input"
          />
          <button type="submit" className="button primary">
            Search
          </button>
          {searchName && (
            <button
              type="button"
              className="button secondary"
              onClick={() => { setSearchName(''); loadPage(1, ''); }}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            className="button primary"
            style={{ marginLeft: '20px' }}
            disabled={syncAllProgress.running || syncingId !== null}
            onClick={handleSyncAll}
          >
            {syncAllProgress.running
              ? `Syncing ${syncAllProgress.current}/${syncAllProgress.total}...`
              : 'Sync All Agency Info'}
          </button>
          <button
              className="button primary"
              onClick={() => navigate('/admin/addtalent')}
              style={{ textDecoration: "none", alignSelf: "start" }}
            >
              Add Talent
            </button>
        </form>
        {syncAllProgress.running && (
          <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '5px' }}>
            <strong>Syncing:</strong> {syncAllProgress.current} of {syncAllProgress.total} |
            <span style={{ color: 'green', marginLeft: '10px' }}>Success: {syncAllProgress.success}</span> |
            <span style={{ color: 'red', marginLeft: '10px' }}>Failed: {syncAllProgress.failed}</span>
          </div>
        )}
        {!syncAllProgress.running && syncAllProgress.total > 0 && (
          <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#d4edda', borderRadius: '5px' }}>
            <strong>Sync Complete:</strong>
            <span style={{ color: 'green', marginLeft: '10px' }}>Success: {syncAllProgress.success}</span> |
            <span style={{ color: 'red', marginLeft: '10px' }}>Failed: {syncAllProgress.failed}</span>
          </div>
        )}
        {error && (
          <div className="talent-list-simple-message error">
            {error}
          </div>
        )}
        <div className="talent-list-simple-table-wrap">
          <table className="talent-list-simple-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Lano ID</th>
                <th>Email</th>
                <th>Funktion</th>
                <th>Agentur</th>
                <th>AP</th>
                <th>Is Agency</th>
                <th>VAT</th>
                <th>Agency VAT</th>
                <th>Country</th>
                <th>Sync</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {talents.map((t) => (
                <tr key={t.id}>
                  <td>{display(t.Name || t.name)}</td>
                  <td>{display(t.Lano_ID || t.lano_id)}</td>
                  <td>{display(t.E_Mail || t.email)}</td>
                  <td>{display(t.Funktion)}</td>
                  <td>{display(t.Agentur)}</td>
                  <td>{display(t.AP)}</td>
                  <td>{(t.is_agency === 1 || t.is_agency === true) ? 'Yes' : 'No'}</td>
                  <td>{vatStatusLabel(t.vat_status)}</td>
                  <td>{agencyVatLabel(t.agency_vat_status)}</td>
                  <td>{display(t.country)}</td>
                  <td>
                    <button
                      type="button"
                      className="button secondary"
                      style={{ padding: '4px 8px', fontSize: '12px' }}
                      disabled={syncingId === t.id || !(t.Lano_ID || t.lano_id)}
                      onClick={() => handleSyncAgency(t)}
                    >
                      {syncingId === t.id ? 'Syncing...' : 'Sync'}
                    </button>
                    {syncMessage.id === t.id && (
                      <span style={{
                        marginLeft: '5px',
                        fontSize: '11px',
                        color: syncMessage.type === 'success' ? 'green' : 'red'
                      }}>
                        {syncMessage.text}
                      </span>
                    )}
                  </td>
                  <td>
                    <Link to={`/admin/talent-list-simple/edit/${t.id}`} className="talent-list-simple-link-billing">
                      Billing
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.totalPages > 1 && (
          <div className="talent-list-simple-pagination">
            <button
              type="button"
              className="button secondary"
              disabled={pagination.currentPage <= 1}
              onClick={() => handlePage(pagination.currentPage - 1)}
            >
              Previous
            </button>
            <span>
              Page {pagination.currentPage} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <button
              type="button"
              className="button secondary"
              disabled={pagination.currentPage >= pagination.totalPages}
              onClick={() => handlePage(pagination.currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TalentListSimple;
